import { ReactNode, useEffect } from 'react';
import { JSXElement } from '@/common/types';
import { useGetEnvironments } from '@/features/environment/hooks/get-tenant-environments';
import { getDefaultEnvironment } from '@/features/environment/utils/environment-utils';
import { useEnvironmentUrlState } from '@/hooks/url-search-params/use-environment-url-state';
import { DEFAULT_ENVIRONMENT } from '@/utils/constants';
import { EnvironmentContext } from './environment-context-types';

interface EnvironmentProviderProps {
    children: ReactNode;
}

const STORAGE_KEY = 'selectedEnvironment';

export function EnvironmentProvider({ children }: EnvironmentProviderProps): JSXElement {
    const { data: environments = [] } = useGetEnvironments();

    const defaultEnvironment = getDefaultEnvironment(environments, DEFAULT_ENVIRONMENT);

    const [urlEnvironment, setUrlEnvironment] = useEnvironmentUrlState();

    /**
     * Get current environment with prioritization:
     * 1. If it's in the URL, use that value
     * 2. If it's not in the URL but sessionStorage has a value, use that value
     * 3. If no value is found, use the default value
     */
    const getCurrentEnvironment = (): string => {
        if (urlEnvironment) {
            return urlEnvironment;
        }

        const sessionStorageEnvironment = sessionStorage.getItem(STORAGE_KEY);
        if (sessionStorageEnvironment) {
            return sessionStorageEnvironment;
        }

        return defaultEnvironment;
    };

    const setSelectedEnvironment = (environment: string): void => {
        sessionStorage.setItem(STORAGE_KEY, environment);
        setUrlEnvironment(environment);
    };

    useEffect(() => {
        if (urlEnvironment) {
            sessionStorage.setItem(STORAGE_KEY, urlEnvironment);
            return;
        }

        const sessionStorageEnvironment = sessionStorage.getItem(STORAGE_KEY);
        if (sessionStorageEnvironment) {
            setUrlEnvironment(sessionStorageEnvironment);
            return;
        }

        if (defaultEnvironment) {
            setUrlEnvironment(defaultEnvironment);
            sessionStorage.setItem(STORAGE_KEY, defaultEnvironment);
        }
    }, [urlEnvironment, defaultEnvironment, setUrlEnvironment]);

    return (
        <EnvironmentContext.Provider
            value={{
                selectedEnvironment: getCurrentEnvironment(),
                setSelectedEnvironment,
            }}
        >
            {children}
        </EnvironmentContext.Provider>
    );
}
